import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  GraphMember,
  GraphMemory,
  GraphPhoto,
  RELATIONSHIP_OPTIONS,
  useFamilyGraph,
  Relationship,
  Side,
  EntryKind,
  LIFE_STAGES,
  LifeStageId,
  resolveLifeStage,
} from '@/contexts/FamilyGraphContext';
import type {
  CanonicalViewerMemberProjection,
} from '@/governor/tree/CanonicalTreeViewerProjection';
import type {
  CanonicalTreeRelationshipEdge,
} from '@/governor/tree/CanonicalTreeRelationshipEdge';
import {
  projectCanonicalTreeProfileDirectConnections,
} from '@/governor/tree/CanonicalTreeProfileDirectConnectionsAuthority';
import { useAuth } from '@/contexts/AuthContext';
import { useMembership } from '@/contexts/MembershipContext';
import {
  projectProfileOwnershipAuthority,
} from '@/governor/profile-ownership/ProfileOwnershipAuthority';
import type {
  CanonicalTreeDeletionErrorClass,
} from '@/governor/tree/CanonicalTreeErrorOnlyDeletionAuthority';

import {
  resolveMemberCommunicationIdentity,
} from '@/governor/communication/ClaimedCommunicationIdentityResolver';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/sonner';
import MemberAvatar from './MemberAvatar';
import MemoryComposerModal from './MemoryComposerModal';
import MediaUploadModal from './MediaUploadModal';
import MediaLightbox from './MediaLightbox';
import EditProfileModal from './EditProfileModal';
import CommentsThread from './CommentsThread';
import ConnectUserModal from './ConnectUserModal';

import { supabase } from '@/lib/supabase';
import { formatBirthday, extractBirthday } from '@/lib/birthday';

import {
  X,
  ShieldCheck,
  Trash2,
  MapPin,
  Calendar,
  Heart,
  UserPlus,
  Send,
  CheckCheck,
  ImageIcon,
  Film,
  BookOpen,
  Clock as ClockIcon,
  Crosshair,
  CalendarHeart,
  Users as UsersIcon,
  Loader2,
  Upload,
  MessageSquarePlus,
  ImagePlus,
  VideoIcon,
  PhoneCall,
  HeartHandshake,
  Sparkles,
  Lock,
  Share2,
  Globe,
  Link2,
} from 'lucide-react';




const relLabel = (r: string) =>
  RELATIONSHIP_OPTIONS.find((o) => o.value === r)?.label || r;

const sideLabel = (s: string) =>
  s === 'mother'
    ? "Mother's side"
    : s === 'father'
    ? "Father's side"
    : s === 'self'
    ? 'Your side'
    : 'Both / shared';

const relationshipPath = (m: GraphMember) =>
  `You → ${sideLabel(m.side)} → ${relLabel(m.relationship)}`;

// ISSUE 1 FIX (DOB one-day shift, Dec 2026 production):
//   The old `formatFullDOB` did `new Date(m.birthDate)` on a YYYY-MM-DD
//   string and then `toLocaleDateString()`. `new Date("1984-10-28")`
//   parses as UTC midnight; the locale formatter rebases to local time,
//   which for any timezone west of UTC rolls the calendar day BACK ONE.
//   That's why entering 10/28/1984 displayed as October 27, 1984.
//
//   Fix: delegate to `formatBirthday` from `@/lib/birthday`, which parses
//   the YYYY-MM-DD string manually (no Date object touches the calendar
//   day) and honors `birthdayVisibility`. Returns:
//     • "October 28, 1984"  for full DOB (matches the user's input)
//     • "October 28"        for month/day only
//     • "1984"              for year_only visibility OR year-only records
//     • "Private"           for private visibility
//     • "Not recorded"      when no DOB info exists (preserves legacy UX)
const formatFullDOB = (m: GraphMember): string => {
  const formatted = formatBirthday(m);
  return formatted || 'Not recorded';
};

// ISSUE 1 FIX (partial DOB → Date for chronological journey sort):
//   Previously called `new Date(m.birthDate)` which UTC-parses and then
//   any local-time rebase shifts the day. Now we build the Date from
//   explicit Y/M/D using `Date.UTC(...)` so the calendar day cannot drift
//   when the date is serialized back via `toISOString().slice(0, 10)` in
//   the timeline (which is rendered via `j.date.toLocaleDateString` —
//   still local-formatted, but UTC-anchored so the day no longer rolls).
const partialDOBToDate = (m: GraphMember): Date | null => {
  const { year, month, day } = extractBirthday(m);
  if (year != null) {
    const mm = (month ?? 1) - 1;
    const dd = day ?? 1;
    return new Date(Date.UTC(year, mm, dd, 12, 0, 0));
  }
  return null;
};


interface Props {
  member: GraphMember | null;
  canonicalViewerProjections:
    ReadonlyMap<
      string,
      CanonicalViewerMemberProjection
    >;
  canonicalRelationshipEdges:
    readonly CanonicalTreeRelationshipEdge[];
  onClose: () => void;
  isStaff: boolean;
  youName: string;
  onAddRelative: (input: { relationship: Relationship; side: Side; prefillName?: string }) => void;
  onCenterOnMember?: (m: GraphMember) => void;
  /**
   * Called when the user taps "Invite to claim this profile" on an unclaimed
   * placeholder. The parent should open the invite modal with the person's
   * name + relationship pre-filled into the personal note ("invite with
   * purpose" — the recipient sees a targeted message that names the role
   * they were placed as in the tree).
   */
  onInvitePerson?: (member: GraphMember) => void;
  /**
   * Called when the user taps "Edit Relationship" on a non-self profile.
   * The parent should open the existing SetRelationshipModal flow seeded
   * with this member so the user can correct the stored relationship +
   * side and (optionally) link parent connectors. Pure additive — when
   * the prop is omitted the button is hidden, so legacy call sites keep
   * working unchanged. Never modifies any data on its own.
   */
  onEditRelationship?: (member: GraphMember) => void;
  onMessagePerson?: (member: GraphMember) => void;
  /**
   * What primary action to focus when the drawer opens. Set when the user
   * picked "Add Memory" from the tree's quick-action menu, or similar.
   *   - 'profile' (or undefined): just open the profile
   *   - 'memory': scroll to & focus the memory composer
   *   - 'photo' / 'video': trigger the file picker for that media type
   */
  initialAction?: 'profile' | 'memory' | 'photo' | 'video';
}




interface JourneyEntry {
  id: string;
  // The actual moment in time this entry represents (event_date or creation).
  date: Date | null;
  // True when we have a real date_precision other than 'unknown'/null event_date.
  hasDate: boolean;
  kind: 'birthday' | 'memory' | 'story' | 'life_event' | 'photo' | 'video' | 'added' | 'biography';
  title: string;
  body?: string;
  url?: string;
  location?: string | null;
  authorLabel?: string;
  // For attaching CommentsThread.
  entryType?: 'memory' | 'photo';
  entryId?: string;
  // Resolved life-stage bucket id.
  stage: LifeStageId;
}

const ProfileDetailDrawer: React.FC<Props> = ({
  member,
  canonicalViewerProjections,
  canonicalRelationshipEdges,
  onClose,
  isStaff,
  youName,
  onAddRelative,
  onCenterOnMember,
  onInvitePerson,
  onEditRelationship,
  onMessagePerson,
  initialAction,
}) => {


  const {
    members,
    memories,
    photos,
    addMemory,
    deletePhoto,
    deleteMember,
    approveMember,
    rejectMember,
    confirmMember,
    addPhoto,
    updateMember,
    bindViewerToMember,
    refresh,
    // BUG 2 FIX — pull the canonical viewer-member id straight from the
    // graph context so we can recognise the logged-in user's own row even
    // in a SHARED family tree where the row's ownerUserId points at a
    // relative (the legacy heuristics below would miss it).
    viewerMemberId: viewerMemberIdFromCtx,
  } = useFamilyGraph();
  const { user } = useAuth();
  const { isMember } = useMembership();

  const [memText, setMemText] = useState('');
  const [savingMem, setSavingMem] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'media' | 'journey'>('overview');

  // Memory composer textarea — focused when the user picked "Add Memory".
  const memoryTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  // Multi-file media upload modal — opens for either photos or videos and
  // lets the user pick many files at once with previews + per-file progress.
  const [mediaModal, setMediaModal] = useState<null | 'image' | 'video'>(null);
  // Full-screen lightbox for tapping a photo / video in the gallery.
  const [lightbox, setLightbox] = useState<{ items: GraphPhoto[]; index: number } | null>(null);
  // Rich memory / story / life-event composer.
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerKind, setComposerKind] = useState<EntryKind>('memory');
  // Governed personal-profile editor. Access is projected by
  // ProfileOwnershipAuthority rather than inferred from UI labels.
  const [editProfileOpen, setEditProfileOpen] = useState(false);
  // Owner/admin "Connect Existing User to This Tree" repair modal — opens
  // from the Overview tab on a non-self, currently-unclaimed profile.
  const [connectUserOpen, setConnectUserOpen] = useState(false);

  const [
    removeErroneousRecordOpen,
    setRemoveErroneousRecordOpen,
  ] = useState(false);

  const [
    deletionErrorClass,
    setDeletionErrorClass,
  ] = useState<
    Exclude<
      CanonicalTreeDeletionErrorClass,
      'FAILED_CREATION'
    > | ''
  >('');

  const [
    deletionExplanation,
    setDeletionExplanation,
  ] = useState('');

  const [
    removingErroneousRecord,
    setRemovingErroneousRecord,
  ] = useState(false);


  const openComposer = (kind: EntryKind) => {
    if (
      !profileAuthority.canContributeProfileMemories
    ) {
      toast.error(
        'This claimed profile can only be updated by its owner.',
      );
      return;
    }

    setComposerKind(kind);
    setComposerOpen(true);
  };

  const openPhotoUpload = () => {
    if (!profileAuthority.canUploadPhotos) {
      if (
        profileAuthority.reason ===
          'CLAIMED_PROFILE_LOCKED'
      ) {
        toast.error(
          'This claimed profile and its media can only be changed by its owner.',
        );
        return;
      }

      if (
        profileAuthority.remainingPhotoCount === 0
      ) {
        toast.error(
          'Family Contributor photo limit reached. Upgrade to add more memories.',
        );
        return;
      }

      toast.error(
        'You do not have authority to add photos to this profile.',
      );
      return;
    }

    setMediaModal('image');
  };

  const openVideoUpload = () => {
    if (!profileAuthority.canUploadVideos) {
      if (
        profileAuthority.reason ===
          'CLAIM_OWNER_FAMILY_CONTRIBUTOR'
      ) {
        toast.error(
          'Video uploads require a sponsored or self-paid membership.',
        );
        return;
      }

      toast.error(
        'This claimed profile and its media can only be changed by its owner.',
      );
      return;
    }

    setMediaModal('video');
  };

  // When the drawer opens with an `initialAction`, react to it: open composer,
  // or pop open the multi-file upload modal.
  useEffect(() => {
    if (!member) return;
    if (initialAction === 'memory') {
      setActiveTab('journey');
      window.setTimeout(() => openComposer('memory'), 80);
    } else if (initialAction === 'photo') {
      window.setTimeout(() => openPhotoUpload(), 80);
    } else if (initialAction === 'video') {
      window.setTimeout(() => openVideoUpload(), 80);
    }
    // We intentionally only react to the member id + action — re-focusing on
    // every member-data refresh would steal focus while typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member?.id, initialAction]);




  const memberMemories = useMemo(
    () => (member ? memories.filter((mm) => mm.memberId === member.id) : []),
    [memories, member]
  );
  const memberMedia = useMemo(
    () => {
      if (!member) return [] as GraphPhoto[];
      // A photo / video appears on this person's profile when EITHER:
      //   (a) it was uploaded with this member as the anchor profile
      //       (`memberId` matches), OR
      //   (b) it was assigned to this member via the "Who is this
      //       memory about?" subject selector (their id lives in
      //       `taggedMemberIds` / `subjectMemberIds`, which mirror the
      //       same `tagged_member_ids` column).
      // The same row can satisfy both — we de-dupe by photo id so the
      // file is shown ONCE even when it's both anchored here AND tagged
      // here. This is what makes the "store the file once, show it on
      // every assigned person's timeline" rule work without duplicating
      // the underlying media.
      const seen = new Set<string>();
      const result: GraphPhoto[] = [];
      for (const p of photos) {
        const subjects = p.subjectMemberIds ?? p.taggedMemberIds ?? [];
        const matches =
          p.memberId === member.id || subjects.includes(member.id);
        if (!matches) continue;
        if (seen.has(p.id)) continue;
        seen.add(p.id);
        result.push(p);
      }
      return result;
    },
    [photos, member]
  );

  // BUG FIX — split memberMedia into photos vs. videos so the Media tab
  // doesn't crash with "memberPhotos is not defined" the moment the user
  // taps the tab. The previous build referenced these names without ever
  // declaring them, which threw a ReferenceError mid-render and blanked
  // the whole drawer (and the tree page mounting it). Pure read; no DB.
  const memberPhotos = useMemo(
    () => memberMedia.filter((p) => p.mediaType !== 'video'),
    [memberMedia]
  );
  const memberVideos = useMemo(
    () => memberMedia.filter((p) => p.mediaType === 'video'),
    [memberMedia]
  );

  // BUG FIX — validate a media URL before handing it to <img>/<video>.
  // A null / empty / "undefined" string would still render a broken
  // element; some legacy rows have stale storage paths that fail to
  // load. We accept http(s), blob:, and data: schemes (the same ones
  // produced by the upload pipeline). Anything else is rejected so the
  // UI can show the "This memory could not be loaded." fallback.
  const isSafeMediaUrl = (u: string | null | undefined): boolean => {
    if (!u || typeof u !== 'string') return false;
    const trimmed = u.trim();
    if (!trimmed) return false;
    return (
      trimmed.startsWith('http://') ||
      trimmed.startsWith('https://') ||
      trimmed.startsWith('blob:') ||
      trimmed.startsWith('data:')
    );
  };



  // Build a chronological "Journey through their life" timeline.
  const journey: JourneyEntry[] = useMemo(() => {
    if (!member) return [];
    const out: JourneyEntry[] = [];
    const dob = partialDOBToDate(member);
    if (dob) {
      out.push({
        id: 'birth', date: dob, hasDate: true,
        kind: 'birthday', title: 'Born', body: formatFullDOB(member),
        stage: resolveLifeStage(member, dob.toISOString().slice(0, 10)),
      });
    }
    if (member.notes) {
      out.push({
        id: 'bio', date: new Date(member.createdAt), hasDate: true,
        kind: 'biography', title: 'Biography added', body: member.notes,
        stage: resolveLifeStage(member, member.createdAt),
      });
    }
    out.push({
      id: 'added', date: new Date(member.createdAt), hasDate: true,
      kind: 'added',
      title: `Added to the family by ${member.createdBy === member.ownerUserId ? youName : 'a family member'}`,
      stage: resolveLifeStage(member, member.createdAt),
    });
    for (const mm of memberMemories) {
      const eventIso = mm.eventDate || null;
      const dateForSort = eventIso ? new Date(eventIso) : (mm.datePrecision === 'unknown' ? null : new Date(mm.createdAt));
      out.push({
        id: `m-${mm.id}`,
        date: dateForSort,
        hasDate: !!eventIso && mm.datePrecision !== 'unknown',
        kind: (mm.entryKind === 'story' ? 'story' : mm.entryKind === 'life_event' ? 'life_event' : 'memory'),
        title: mm.title || (mm.entryKind === 'life_event' ? 'Life event' : mm.entryKind === 'story' ? 'Story' : 'Memory'),
        body: mm.text,
        location: mm.location,
        stage: resolveLifeStage(member, eventIso, mm.lifeStage),
        entryType: 'memory',
        entryId: mm.id,
      });
    }
    for (const p of memberMedia) {
      const eventIso = p.eventDate || null;
      const dateForSort = eventIso ? new Date(eventIso) : (p.datePrecision === 'unknown' ? null : new Date(p.createdAt));
      out.push({
        id: `p-${p.id}`,
        date: dateForSort,
        hasDate: !!eventIso && p.datePrecision !== 'unknown',
        kind: p.mediaType === 'video' ? 'video' : 'photo',
        title: p.title || (p.mediaType === 'video' ? 'Video added' : 'Photo added'),
        body: p.caption || undefined,
        url: p.url,
        location: p.location,
        stage: resolveLifeStage(member, eventIso),
        entryType: 'photo',
        entryId: p.id,
      });
    }
    // Sort: dated entries chronologically; undated go last.
    return out.sort((a, b) => {
      if (a.date && b.date) return a.date.getTime() - b.date.getTime();
      if (a.date) return -1;
      if (b.date) return 1;
      return 0;
    });
  }, [member, memberMemories, memberMedia, youName]);


  if (!member) return null;
  const verified = member.approvalStatus === 'approved';
  const pending = member.approvalStatus === 'pending';
  const sideForKin: Side =
    member.side === 'self' || member.side === 'both' ? 'both' : member.side;

  // Single source of truth for "is this profile already bound to a real
  // user account?". We treat EITHER a profile_state of 'claimed' OR a
  // non-null claimed_by_user_id as claimed, so the inline repair action
  // never appears on a row that's already been bound (defensive against
  // legacy rows where one column drifted out of sync with the other).
  const isClaimed = member.profileState === 'claimed' || !!member.claimedByUserId;

  const communicationIdentityInput = () => ({
    memberId: member.id,
    profileState:
      member.profileState ??
      null,
    claimedByUserId:
      member.claimedByUserId ??
      null,
    directEmail:
      (
        (member as any).email ||
        (member as any).member_email ||
        (member as any).user_email ||
        (member as any).authEmail ||
        (member as any).auth_email ||
        ''
      ).trim(),
  });

  const resolveCommunicationIdentity =
    async () => {
      const result =
        await resolveMemberCommunicationIdentity(
          communicationIdentityInput(),
        );

      if (!result.ok) {
        toast.error(
          result.reason,
        );

        return null;
      }

      return result;
    };

  const startNativeCall = async (
    mode: 'voice' | 'video',
  ) => {
    const identity =
      await resolveCommunicationIdentity();

    if (!identity) {
      return;
    }

    const nativeCallUrl =
      `/native-call?mode=${mode}` +
      `&to=${encodeURIComponent(
        identity.email,
      )}` +
      `&name=${encodeURIComponent(
        member.name ||
          'Family member',
      )}`;

    window.history.pushState(
      {
        source:
          'ProfileDetailDrawer.startNativeCall',
        memberId:
          identity.memberId,
        claimedUserId:
          identity.claimedUserId,
        identitySource:
          identity.source,
      },
      '',
      nativeCallUrl,
    );

    window.dispatchEvent(
      new PopStateEvent(
        'popstate',
      ),
    );
  };

  const startProfileMessage =
    async () => {
      if (!onMessagePerson) {
        return;
      }

      const identity =
        await resolveCommunicationIdentity();

      if (!identity) {
        return;
      }

      const resolvedMember = {
        ...member,
        email:
          identity.email,
        authEmail:
          identity.email,
        auth_email:
          identity.email,
        claimedByUserId:
          identity.claimedUserId ??
          member.claimedByUserId,
      } as GraphMember;

      onMessagePerson(
        resolvedMember,
      );

      onClose();
    };


  // Identify the user's own profile so we can:
  //  - show "You" instead of "Unknown / Possible Relation"

  //  - show "You" instead of "Unknown / Possible Relation"
  //  - show the Update My Legacy button + grant edit access
  //  - fall back to a real display name if the row's `name` is blank
  //
  // Detection is intentionally broad — ANY of these conditions makes the
  // logged-in user the owner of this profile:
  //   1. The synthesized "You" tree node (id === 'self')
  //   2. A profile claimed by the current auth user
  //   3. A side='self' row created by the current auth user (legacy /
  //      pre-claim accounts where the claim flag was never written)
  //   4. The canonical viewer-member id resolved by FamilyGraphContext
  //      (covers shared graphs where the viewer's "self" row lives in
  //      someone else's graph and may not match conditions 2-3 directly)
  // We deliberately do NOT require `relationship === 'unknown'` here — older
  // self-anchor rows sometimes had stray relationship values and were being
  // silently treated as not-mine, hiding the Update My Legacy button.
  // BUG 2 FIX — also recognise the canonical viewerMemberId from context
  // so a claimed self-row in a SHARED graph (where ownerUserId points at
  // a relative) is still treated as "this is your profile".
  const isMyProfile =
    !!user &&
    (
      member.id === 'self' ||
      (!!viewerMemberIdFromCtx && member.id === viewerMemberIdFromCtx) ||
      member.claimedByUserId === user.id
    );

  // Best-available display name. Order:
  //   1. The row's name (unless it's literally "Unknown" or empty)
  //   2. youName from auth (full name from profiles)
  //   3. Email username
  //   4. The string "You"
  // For OWN profile we ALWAYS prefer the auth name over a stale "Unknown"
  // row value so the UI never displays "Unknown" for the logged-in user
  // (rule 2 — "do not wait for backend merge to complete").
  const rowName =
    member.name && member.name.trim() && member.name.trim().toLowerCase() !== 'unknown'
      ? member.name.trim()
      : null;
  const authFallbackName =
    youName ||
    (user?.email ? user.email.split('@')[0] : null) ||
    'You';
  const displayName = isMyProfile
    ? rowName || authFallbackName
    : rowName || 'Unnamed';

  // Relationship label — never show "Unknown / Possible Relation" for the
  // user's own profile; show "You" instead. (BUG 2 fix — this single
  // resolved string is now the ONLY relationship label rendered anywhere
  // in the drawer for the viewer's own profile, including the facts grid
  // and the "Relationship to you" tile, so a stale `relationship='unknown'`
  // value on the self-anchor row can never leak the literal "Unknown
  // relation" / "Unknown / Possible Relation" string into the UI.)
  //
  // UNIVERSAL LABEL FIX — for non-self profiles, prefer the generational
  // kinship engine's parent_ids-derived label (same engine the tree uses)
  // so the drawer shows "Sister" / "Aunt" / "Great-Grandfather" / "Second
  // cousin once removed" instead of the stored bucket label whenever
  // parent_ids prove the path. This makes the drawer agree with the tree
  // row label exactly. Falls back to the stored enum bucket label when
  // the engine can't structurally prove the relationship (so we never
  // surface "Unknown relation" — the spec rule). Pure read; no DB writes.
  const relationshipDisplay = isMyProfile
    ? 'You'
    : canonicalViewerProjections
        .get(member.id)
        ?.kinshipSemantics.primary.preciseLabel
        ?.trim() ||
      'Relationship unresolved';

  const canonicalConnectionPath =
    isMyProfile
      ? []
      : canonicalViewerProjections
          .get(member.id)
          ?.kinshipSemantics.primary.connectionPath ??
        [];

  const canonicalEvidenceEdgeIds =
    isMyProfile
      ? []
      : canonicalViewerProjections
          .get(member.id)
          ?.kinshipSemantics.primary.evidenceEdgeIds ??
        [];

  const canonicalPathDisplay =
    canonicalConnectionPath.length === 0
      ? null
      : [
          'You',
          ...canonicalConnectionPath.map(
            step =>
              members.find(
                candidate =>
                  candidate.id ===
                  step.toMemberId,
              )?.name ??
              'Family member',
          ),
        ].join(' → ');

  const canonicalDirectConnections =
    projectCanonicalTreeProfileDirectConnections({
      members,
      subjectMemberId: member.id,
      viewerMemberId:
        viewerMemberIdFromCtx ?? null,
      edges: canonicalRelationshipEdges,
    });

  const directConnectionGroups = [
    {
      id: 'parents',
      label: 'Parents',
      people: canonicalDirectConnections.parents,
    },
    {
      id: 'siblings',
      label: 'Recorded siblings',
      people: canonicalDirectConnections.siblings,
    },
    ...(isMyProfile
      ? [{
          id: 'children',
          label: 'Children',
          people: canonicalDirectConnections.children,
        }]
      : canonicalDirectConnections.sharedChildrenWithViewer.length > 0
        ? [
            {
              id: 'shared-children',
              label: 'Shared children with you',
              people: canonicalDirectConnections.sharedChildrenWithViewer,
            },
            {
              id: 'other-children',
              label: 'Other recorded children',
              people: canonicalDirectConnections.otherRecordedChildren,
            },
          ]
        : [{
            id: 'children',
            label: 'Children',
            people: canonicalDirectConnections.children,
          }]),
    {
      id: 'partners',
      label: 'Spouse / partner',
      people: canonicalDirectConnections.currentPartners,
    },
    {
      id: 'former-partners',
      label: 'Former partner / spouse',
      people: canonicalDirectConnections.formerPartners,
    },
  ].filter(group => group.people.length > 0);

  const ownedPhotoUrls = new Set<string>();

  if (member.photoUrl) {
    ownedPhotoUrls.add(member.photoUrl);
  }

  for (const photo of memberPhotos) {
    if (photo.url) {
      ownedPhotoUrls.add(photo.url);
    }
  }

  const profileAuthority =
    projectProfileOwnershipAuthority({
      profileState: member.profileState,
      claimedByUserId:
        member.claimedByUserId ?? null,
      currentUserId: user?.id ?? null,
      createdByUserId:
        member.createdBy ?? null,
      graphOwnerUserId:
        member.ownerUserId ?? null,
      isGraphStaff: isStaff,
      hasPaidEntitlement: isMember,
      existingPhotoCount:
        ownedPhotoUrls.size,
    });

  const canEditProfile =
    profileAuthority.canEditPersonalInfo &&
    member.id !== 'self';

  const openProfileEditor = () => {
    if (!canEditProfile) {
      toast.error(
        isClaimed
          ? 'Only the claimed profile owner may edit personal information.'
          : 'You do not have authority to edit this profile.',
      );
      return;
    }

    setEditProfileOpen(true);
  };

  const handleDeleteProfileMedia = async (
    mediaId: string,
    mediaLabel: 'photo' | 'video',
  ) => {
    if (!profileAuthority.canDeleteMedia) {
      toast.error(
        'Only the claimed profile owner may remove this media.',
      );
      return;
    }

    if (
      !confirm(
        `Remove this ${mediaLabel}?`,
      )
    ) {
      return;
    }

    try {
      await deletePhoto(mediaId);
      toast.success(
        mediaLabel === 'photo'
          ? 'Photo removed'
          : 'Video removed',
      );
    } catch (error: any) {
      toast.error(
        error?.message ||
          `Could not remove ${mediaLabel}`,
      );
    }
  };


  const submitMemory = async () => {
    if (!profileAuthority.canContributeProfileMemories) {
      toast.error(
        'Only the claimed profile owner may add memories to this profile.',
      );
      return;
    }

    if (!memText.trim()) return;
    setSavingMem(true);
    try {
      await addMemory(member.id, memText.trim());
      setMemText('');
      toast.success('Memory saved');
    } catch (err: any) {
      toast.error(err?.message || 'Could not save memory');
    } finally {
      setSavingMem(false);
    }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await confirmMember(member.id);
      toast.success(
        member.confirmationsCount + 1 >= 2 && !verified
          ? `${member.name} verified by 2 members!`
          : `Confirmed ${member.name}`
      );
    } catch (err: any) {
      toast.error(err?.message || 'Could not confirm');
    } finally {
      setConfirming(false);
    }
  };

  const handleApprove = async () => {
    try {
      await approveMember(member.id);
      toast.success(`${member.name} verified`);
      onClose();
    } catch (err: any) {
      toast.error(err?.message || 'Approve failed');
    }
  };
  const handleReject = async () => {
    try {
      await rejectMember(member.id);
      toast.success(`${member.name} rejected`);
      onClose();
    } catch (err: any) {
      toast.error(err?.message || 'Reject failed');
    }
  };
  const isProtectedProfile =
    member.id === 'self' ||
    [
      'self',
      'mother',
      'father',
      'grandparent',
      'great-grandparent',
    ].includes(
      String(
        member.relationship || '',
      ).toLowerCase(),
    );

  const canShowRemoveAction =
    isStaff &&
    !isClaimed &&
    !isProtectedProfile;

  const handleDelete = async () => {
    if (!deletionErrorClass) {
      toast.error(
        'Select why this record is erroneous.',
      );
      return;
    }

    const explanation =
      deletionExplanation.trim();

    if (!explanation) {
      toast.error(
        'Explain why this record was created in error.',
      );
      return;
    }

    setRemovingErroneousRecord(true);

    try {
      await deleteMember({
        memberId: member.id,
        errorClass: deletionErrorClass,
        explanation,
      });

      toast.success(
        `${member.name} erroneous record removed`,
      );

      setRemoveErroneousRecordOpen(false);
      setDeletionErrorClass('');
      setDeletionExplanation('');
      onClose();
    } catch (error: any) {
      toast.error(
        error?.message ||
          'This record cannot be removed. Use Edit Profile, Correct Relationship, or Merge Duplicate.',
      );
    } finally {
      setRemovingErroneousRecord(false);
    }
  };

  const childRelFor = (m: GraphMember): Relationship => {
    if (m.relationship === 'mother' || m.relationship === 'father') return 'sibling';
    if (m.relationship === 'sibling' || m.relationship === 'spouse') return 'cousin-child';
    return 'cousin-child';
  };
  const parentRelFor = (m: GraphMember): Relationship => {
    if (m.relationship === 'mother' || m.relationship === 'father') return 'grandparent';
    if (m.relationship === 'grandparent') return 'great-grandparent';
    return 'aunt-uncle';
  };

  const livingLabel =
    member.livingStatus === 'deceased'
      ? 'In memoriam'
      : member.livingStatus === 'living'
      ? 'Living'
      : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-2xl lg:max-w-5xl bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[94vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >

        {/* Header — large profile photo + name + relationship */}
        <div className="bg-gradient-to-br from-fuchsia-600 via-pink-500 to-amber-400 text-white p-6 flex items-start gap-4">
          <div className="shrink-0">
            <MemberAvatar
              name={displayName}
              photoUrl={member.photoUrl}
              size="lg"
              verified={verified}
              pending={pending}
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-serif font-bold text-2xl sm:text-3xl truncate">
              {displayName}
              {member.nickname && (
                <span className="ml-2 text-base font-normal opacity-90">
                  &ldquo;{member.nickname}&rdquo;
                </span>
              )}
            </div>
            <div className="text-sm opacity-95 mt-0.5">{isMyProfile ? "Your profile" : "Part of your family story"}</div>
            <div className="text-[11px] opacity-90 mt-0.5">
              {isMyProfile
                  ? 'Your living family legacy'
                  : `${relationshipDisplay}${member.city ? ` • ${member.city}` : ''}${livingLabel ? ` • ${livingLabel}` : ''}`}
            </div>

            <div className="mt-2 flex flex-wrap gap-1">
              {verified && (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-emerald-500/95 px-2 py-0.5 rounded-full">
                  <ShieldCheck className="w-3 h-3" /> Verified
                </span>
              )}
              {pending && (
                <span className="text-[10px] font-bold bg-amber-500 px-2 py-0.5 rounded-full">
                  Pending · {member.confirmationsCount}/2 confirmations
                </span>
              )}
              {/* Claim / ownership badge — single source of truth for who
                  manages this profile's personal info. When the profile is
                  unclaimed AND the viewer is owner/admin, we render an
                  inline "Connect" chip right next to the badge so the
                  repair tool is discoverable the moment the unclaimed
                  state is visible.

                  VISIBILITY FIX — the chip used to be hidden when
                  `isMyProfile` was true, which created a stuck state for
                  rows that were simultaneously labeled "You" AND still
                  flagged "Unclaimed" (legacy self-anchor rows where the
                  claim metadata was never written, or shared-graph rows
                  where the viewer's auth id matched ownerUserId but the
                  claim columns were never populated). In that state the
                  user could see "Unclaimed profile" but had no way to
                  fix it. The gate is now `isStaff && !isClaimed` so any
                  owner/admin can repair ANY unclaimed row regardless of
                  whether it's labeled "You". The only remaining guard is
                  `member.id !== 'self'` because the synthetic 'self'
                  placeholder has no DB row for the edge function to
                  write claim metadata to. */}
              {isClaimed ? (
                <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-indigo-500/95 px-2 py-0.5 rounded-full">
                  <ShieldCheck className="w-3 h-3" />
                  {member.claimedByUserId === user?.id ? 'Managed by you' : 'Claimed profile'}
                </span>
              ) : (
                <>
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold bg-white/30 px-2 py-0.5 rounded-full">
                    Unclaimed profile
                  </span>
                  {isStaff && !isClaimed && member.id !== 'self' && (
                    <button
                      type="button"
                      onClick={() => setConnectUserOpen(true)}
                      className="inline-flex items-center gap-1 text-[10px] font-bold bg-white text-indigo-700 hover:bg-indigo-50 px-2 py-0.5 rounded-full shadow-sm transition"
                      title="Connect an existing user account to this profile"
                    >
                      <Link2 className="w-3 h-3" />
                      Connect account
                    </button>
                  )}
                </>
              )}


              <span className="text-[10px] font-semibold bg-white/25 px-2 py-0.5 rounded-full">
                {sideLabel(member.side)}
              </span>
            </div>
          </div>

          <button
            onClick={onClose}
            className="text-white/80 hover:text-white p-1 shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>



        {/* Tabs */}
        <div className="flex border-b border-slate-200 bg-slate-50">
          {[
            { id: 'overview' as const, label: 'Overview' },
            { id: 'media' as const, label: `Photos & Videos (${memberMedia.length})` },
            { id: 'journey' as const, label: 'Journey' },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`flex-1 px-3 py-2.5 text-xs sm:text-sm font-semibold transition ${
                activeTab === t.id
                  ? 'text-fuchsia-700 bg-white border-b-2 border-fuchsia-600'
                  : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {/* Profile contribution actions.
            Claimed profiles are controlled by their owner.
            Unclaimed placeholders remain maintainable by their
            governed creator or family-graph owner. */}
        <div className="px-3 sm:px-5 py-3 border-b border-slate-100 bg-white">
          {profileAuthority.canContributeProfileMemories ? (
            <>
              <div className="grid grid-cols-5 gap-1.5">
                <button
                  type="button"
                  onClick={openPhotoUpload}
                  className="flex flex-col items-center justify-center gap-1 rounded-xl bg-fuchsia-50 hover:bg-fuchsia-100 text-fuchsia-700 border border-fuchsia-200 py-2 px-1 text-[10px] font-bold transition active:scale-[0.98]"
                >
                  <ImagePlus className="w-4 h-4" />
                  Photo
                </button>

                <button
                  type="button"
                  onClick={openVideoUpload}
                  className="flex flex-col items-center justify-center gap-1 rounded-xl bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 py-2 px-1 text-[10px] font-bold transition active:scale-[0.98]"
                >
                  <VideoIcon className="w-4 h-4" />
                  Video
                </button>

                <button
                  type="button"
                  onClick={() =>
                    openComposer('memory')
                  }
                  className="flex flex-col items-center justify-center gap-1 rounded-xl bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 py-2 px-1 text-[10px] font-bold transition active:scale-[0.98]"
                >
                  <Sparkles className="w-4 h-4" />
                  Memory
                </button>

                <button
                  type="button"
                  onClick={() =>
                    openComposer('story')
                  }
                  className="flex flex-col items-center justify-center gap-1 rounded-xl bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200 py-2 px-1 text-[10px] font-bold transition active:scale-[0.98]"
                >
                  <BookOpen className="w-4 h-4" />
                  Story
                </button>

                <button
                  type="button"
                  onClick={() =>
                    openComposer('life_event')
                  }
                  className="flex flex-col items-center justify-center gap-1 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 py-2 px-1 text-[10px] font-bold transition active:scale-[0.98]"
                >
                  <HeartHandshake className="w-4 h-4" />
                  Life Event
                </button>
              </div>

              {isMyProfile && (
                <button
                  type="button"
                  onClick={openProfileEditor}
                  className="mt-2 w-full rounded-xl bg-gradient-to-r from-fuchsia-600 via-pink-500 to-amber-400 hover:opacity-95 text-white font-bold py-2.5 px-3 text-sm shadow inline-flex items-center justify-center gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  Update My Legacy
                </button>
              )}
            </>
          ) : (
            <div className="rounded-xl border border-indigo-100 bg-indigo-50/70 px-4 py-3">
              <div className="flex items-start gap-2">
                <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" />

                <div>
                  <div className="text-xs font-bold text-indigo-900">
                    This profile is personally managed
                  </div>
                  <p className="mt-0.5 text-[11px] leading-relaxed text-indigo-800/80">
                    {displayName} controls their profile,
                    photos, videos, memories, stories,
                    and life events.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">

          {/* OVERVIEW TAB */}
          {activeTab === 'overview' && (
            <div className="lg:grid lg:grid-cols-2 lg:gap-6 space-y-5 lg:space-y-0">
              {/* LEFT COLUMN — identity, facts, biography */}
              <div className="space-y-5">
                {/* Family relationship spotlight */}
              <div className="rounded-2xl bg-gradient-to-r from-[#8b2bd6] via-[#e23aa8] to-[#f6b73c] p-[1px] shadow-md">
                <div className="rounded-2xl bg-white/95 px-4 py-4 flex items-center gap-3">
                  <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#8b2bd6] via-[#e23aa8] to-[#f6b73c] flex items-center justify-center text-2xl shadow-sm">
                    {relationshipDisplay?.toLowerCase().includes('mother') ? '💗' :
                     relationshipDisplay?.toLowerCase().includes('father') ? '💙' :
                     relationshipDisplay?.toLowerCase().includes('grand') ? '👑' :
                     relationshipDisplay?.toLowerCase().includes('sister') ? '💜' :
                     relationshipDisplay?.toLowerCase().includes('brother') ? '💚' :
                     relationshipDisplay?.toLowerCase().includes('spouse') ? '💍' :
                     relationshipDisplay?.toLowerCase().includes('child') ? '🌱' :
                     relationshipDisplay?.toLowerCase().includes('aunt') ? '🌸' :
                     relationshipDisplay?.toLowerCase().includes('uncle') ? '🌿' :
                     '🌳'}
                  </div>
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wide font-bold text-[#8b2bd6]/80">
                      Family Connection
                    </div>
                    <div className="font-serif text-2xl font-bold text-slate-900 truncate">
                      {relationshipDisplay}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Part of your living family legacy
                    </div>
                  </div>
                </div>
              </div>

              {/* Facts grid */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-xl bg-gradient-to-br from-white via-fuchsia-50 to-amber-50 border border-fuchsia-100 px-3 py-3 flex items-center gap-2 text-sm shadow-sm">
                    <Sparkles className="w-4 h-4 text-fuchsia-500" />
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] uppercase text-fuchsia-700/80 font-bold tracking-wide">
                        Nickname / Goes by
                      </div>
                      <div className="font-serif font-bold text-slate-900 truncate">
                        {member.nickname?.trim()
                          ? member.nickname
                          : isMyProfile
                            ? 'Add your nickname'
                            : 'Not added yet'}
                      </div>
                    </div>

                    {isMyProfile && (
                      <button
                        type="button"
                        onClick={openProfileEditor}
                        className="shrink-0 rounded-full border border-fuchsia-200 bg-white px-2 py-1 text-[9px] font-bold uppercase tracking-wide text-fuchsia-700 hover:bg-fuchsia-50"
                      >
                        Edit
                      </button>
                    )}
                  </div>

                  <div className="rounded-xl bg-gradient-to-br from-white via-fuchsia-50 to-amber-50 border border-fuchsia-100 px-3 py-3 flex items-center gap-2 text-sm shadow-sm">
                    <Calendar className="w-4 h-4 text-slate-500" />
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase text-fuchsia-700/80 font-bold tracking-wide">Date of birth</div>
                      <div className="font-serif font-bold text-slate-900 truncate">{member.date_of_birth ? formatFullDOB(member) : 'Add birthday'}</div>
                    </div>
                  </div>
                  <div className="rounded-xl bg-gradient-to-br from-white via-fuchsia-50 to-amber-50 border border-fuchsia-100 px-3 py-3 flex items-center gap-2 text-sm shadow-sm">
                    <Heart className="w-4 h-4 text-rose-500" />
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase text-fuchsia-700/80 font-bold tracking-wide">Status</div>
                      <div className="font-serif font-bold text-slate-900 truncate">{livingLabel}</div>
                    </div>
                  </div>
                  <div className="rounded-xl bg-gradient-to-br from-white via-fuchsia-50 to-amber-50 border border-fuchsia-100 px-3 py-3 flex items-center gap-2 text-sm shadow-sm">
                    <UsersIcon className="w-4 h-4 text-fuchsia-500" />
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase text-fuchsia-700/80 font-bold tracking-wide">Family Branch</div>
                      <div className="font-serif font-bold text-slate-900 truncate">{sideLabel(member.side)}</div>
                    </div>
                  </div>
                  <div className="rounded-xl bg-gradient-to-br from-white via-fuchsia-50 to-amber-50 border border-fuchsia-100 px-3 py-3 flex items-center gap-2 text-sm shadow-sm">
                    <CalendarHeart className="w-4 h-4 text-purple-500" />
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase text-fuchsia-700/80 font-bold tracking-wide">How you're connected</div>
                      <div className="font-serif font-bold text-slate-900 truncate">{relationshipDisplay}</div>
                      {!isMyProfile && canonicalPathDisplay && (
                        <div
                          className="mt-0.5 text-[10px] text-slate-500 truncate"
                          title={
                            canonicalEvidenceEdgeIds.length
                              ? `Canonical evidence: ${canonicalEvidenceEdgeIds.join(', ')}`
                              : undefined
                          }
                        >
                          {canonicalPathDisplay}
                        </div>
                      )}

                    </div>
                  </div>
                  {member.city && (
                    <div className="rounded-xl bg-gradient-to-br from-white via-fuchsia-50 to-amber-50 border border-fuchsia-100 px-3 py-3 flex items-center gap-2 text-sm shadow-sm col-span-2">
                      <MapPin className="w-4 h-4 text-slate-500" />
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase text-fuchsia-700/80 font-bold tracking-wide">Location</div>
                        <div className="font-serif font-bold text-slate-900 truncate">{member.city}</div>
                      </div>
                    </div>
                  )}
                </div>

                {canonicalDirectConnections.hasConnections && (
                  <div className="rounded-2xl border border-violet-200 bg-violet-50/50 p-4 shadow-sm">
                    <div className="flex items-center gap-2">
                      <UsersIcon className="h-4 w-4 text-violet-600" />
                      <div>
                        <div className="text-xs font-bold text-violet-900">
                          Direct family connections
                        </div>
                        <div className="text-[10px] text-violet-700/80">
                          Derived from the canonical family graph
                        </div>
                      </div>
                    </div>

                    <div className="mt-3 space-y-2">
                      {directConnectionGroups.map(group => (
                        <div key={group.id}>
                          <div className="text-[10px] font-bold uppercase tracking-wide text-violet-700/80">
                            {group.label}
                          </div>
                          <div className="mt-1 flex flex-wrap gap-1.5">
                            {group.people.map(connection => (
                              <span
                                key={connection.memberId}
                                className="rounded-full border border-violet-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-800"
                              >
                                {connection.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}

                      {canonicalDirectConnections.modernConnections.length > 0 && (
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-wide text-violet-700/80">
                            Other governed connections
                          </div>
                          <div className="mt-1 space-y-1">
                            {canonicalDirectConnections.modernConnections.map(connection => (
                              <div
                                key={connection.edgeId}
                                className="rounded-lg border border-violet-100 bg-white px-2.5 py-1.5 text-[11px] text-slate-700"
                              >
                                <span className="font-semibold">
                                  {connection.preferredFamilyTitle || connection.edgeKind}
                                </span>
                                {' · '}
                                {connection.otherMember.name}
                                {connection.sharedChildren.length > 0 && (
                                  <span className="text-slate-500">
                                    {' · '}children: {connection.sharedChildren.map(child => child.name).join(', ')}
                                  </span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Center tree on this person */}
                {isMyProfile && onCenterOnMember && (
                  <button
                    onClick={async () => {
            await bindViewerToMember(member.id);
            await refresh();
          }}
                    className="w-full rounded-2xl bg-gradient-to-r from-purple-700 via-pink-500 to-amber-400 hover:opacity-95 text-white font-bold py-3.5 flex items-center justify-center gap-2 shadow-lg"
                  >
                    <Crosshair className="w-4 h-4" />
                    This is me / Center tree on me
                  </button>
                )}

                {/* Edit Profile — personal identity fields only.
                  Permission comes exclusively from ProfileOwnershipAuthority.
                  Claimed profiles are editable only by their claimed owner.
                  Authorized creators / graph authority may edit placeholders
                  until the profile is claimed. Relationship placement remains
                  isolated in Edit Relationship below. */}
              {canEditProfile && (
                <button
                  type="button"
                  onClick={openProfileEditor}
                  className="w-full rounded-2xl bg-gradient-to-r from-fuchsia-50 via-white to-amber-50 border-2 border-fuchsia-300 hover:border-fuchsia-500 text-fuchsia-700 font-bold py-3.5 flex items-center justify-center gap-2 transition shadow-sm"
                  title={`Edit ${displayName}'s personal profile`}
                >
                  <Sparkles className="w-4 h-4" />
                  Edit Profile
                </button>
              )}

              {/* Edit Relationship — safe entry-point into the existing
                    SetRelationshipModal flow. Lets the user correct stored
                    relationship + side and link parent / sibling / cousin
                    connectors WITHOUT touching photos, memories, comments,
                    or the member's identity. The modal itself never deletes,
                    duplicates, or merges — it only writes parent_ids /
                    relationship / side via the existing linkParents +
                    updateMember APIs (no schema changes). Hidden on the
                    user's OWN profile because the viewer's row has no
                    "relationship to self" to edit. */}
                {onEditRelationship && !isMyProfile && member.id !== 'self' && (
                  <button
                    onClick={() => onEditRelationship(member)}
                    className="w-full rounded-2xl bg-gradient-to-r from-emerald-50 to-white border-2 border-emerald-300 hover:border-emerald-500 hover:bg-emerald-50 text-emerald-700 font-bold py-3.5 flex items-center justify-center gap-2 transition shadow-sm"
                    title={`Fix how ${(member?.name || 'Family Member').split(' ')[0]} connects to your family tree`}
                  >
                    <Link2 className="w-4 h-4" />
                    Edit Relationship
                  </button>
                )}


      {!isMyProfile && member.id !== 'self' && (
              <div className="grid grid-cols-3 gap-2">
                <button type="button" onClick={() => startNativeCall('voice')} className="rounded-2xl bg-gradient-to-r from-emerald-50 to-white border-2 border-emerald-300 text-emerald-700 font-bold py-3 flex flex-col items-center justify-center gap-1 shadow-sm">
                  <PhoneCall className="w-4 h-4" />
                  <span className="text-xs">Call</span>
                </button>
                <button type="button" onClick={() => startNativeCall('video')} className="rounded-2xl bg-gradient-to-r from-purple-50 to-white border-2 border-purple-300 text-purple-700 font-bold py-3 flex flex-col items-center justify-center gap-1 shadow-sm">
                  <VideoIcon className="w-4 h-4" />
                  <span className="text-xs">Video</span>
                </button>
                <button type="button" onClick={() => void startProfileMessage()} className="rounded-2xl bg-gradient-to-r from-sky-50 to-white border-2 border-sky-300 text-sky-700 font-bold py-3 flex flex-col items-center justify-center gap-1 shadow-sm">
                  <span className="text-base">💬</span>
                  <span className="text-xs">Message</span>
                </button>
              </div>
            )}
{/* Invite to claim this profile — only shown for unclaimed
                    placeholder profiles (people the user has added on
                    behalf of). Opens the invite modal pre-filled with the
                    person's name + role for the "invite with purpose"
                    targeted-message system. When the recipient signs up via
                    that link, the existing claim system lets them take
                    ownership of this exact profile (auto-claim). */}
                {onInvitePerson &&
                  member.profileState !== 'claimed' &&
                  member.id !== 'self' &&
                  member.relationship !== 'unknown' && (
                    <button
                      onClick={() => onInvitePerson(member)}
                      className="w-full rounded-xl bg-white border-2 border-dashed border-fuchsia-300 hover:border-fuchsia-500 hover:bg-fuchsia-50 text-fuchsia-700 font-semibold py-3 flex items-center justify-center gap-2 transition"
                      title={`Send ${(member?.name || 'Family Member').split(' ')[0]} an invite — when they sign up, they'll be able to claim this profile.`}
                    >
                      <UserPlus className="w-4 h-4" />
                      Invite {(member?.name || 'Family Member').split(' ')[0]} to claim this profile
                    </button>
                  )}

                {/* Connect Existing User to This Tree — owner / admin
                    repair tool. Use this when a relative ALREADY signed up
                    for their own account (separate tree) but their account
                    isn't bound to this shared tree. Binds the existing auth
                    user to this row by writing claimed_by_user_id +
                    profile_state='claimed' + claimed_at + updated_at via
                    the connect-user-to-profile edge function. NEVER touches
                    parent_ids / relationship / side / photos / memories /
                    biography or any other field. NEVER deletes or merges
                    the user's own personal tree — it stays intact, but
                    resolveEffectiveOwner will now load THIS shared tree on
                    their next login because the new claim wins.

                    VISIBILITY FIX — gate is now `isStaff && !isClaimed`
                    (matches the inline header chip). The previous gate
                    additionally required `!isMyProfile`, which created a
                    stuck state for legacy self-anchor rows that were
                    simultaneously labeled "You" AND still flagged
                    "Unclaimed" (the claim metadata was never written, or
                    in shared graphs the row's ownerUserId pointed at a
                    relative). In that state the user could see "Unclaimed
                    profile" with no way to repair it. Owner/admins can now
                    bind an account to ANY unclaimed row regardless of the
                    "You" label. The synthetic 'self' placeholder is still
                    excluded because it has no DB row to write to. */}
                {isStaff &&
                  !isClaimed &&
                  member.id !== 'self' && (

                    <button
                      onClick={() => setConnectUserOpen(true)}
                      className="w-full rounded-xl bg-white border-2 border-indigo-300 hover:border-indigo-500 hover:bg-indigo-50 text-indigo-700 font-semibold py-3 flex items-center justify-center gap-2 transition shadow-sm"
                      title="Connect an existing user account (by email) to this profile."
                    >
                      <Link2 className="w-4 h-4" />
                      Connect existing user to this tree
                    </button>
                  )}




                {/* About / description */}
                {member.description && (
                  <div className="rounded-xl bg-fuchsia-50 border border-fuchsia-100 p-3">
                    <div className="text-xs font-bold text-fuchsia-700 uppercase tracking-wide mb-1">
                      About
                    </div>
                    <div className="text-sm text-slate-800">{member.description}</div>
                  </div>
                )}

                {/* Biography / history */}
                <div className="rounded-xl bg-slate-50 border border-slate-200 p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <BookOpen className="w-4 h-4 text-slate-600" />
                    <div className="text-xs font-bold text-slate-600 uppercase tracking-wide">
                      Family Legacy
                    </div>
                  </div>
                  {member.notes ? (
                    <div className="text-sm text-slate-800 whitespace-pre-wrap">{member.notes}</div>
                  ) : (
                    <>
                      <div className="text-xs text-slate-500 italic">
                        Every family member has a story. Add memories, photos, milestones, and family history to preserve their legacy.
                      </div>
                      <button
                        type="button"
                        onClick={() => openComposer('story')}
                        className="mt-3 w-full rounded-xl bg-gradient-to-r from-[#8b2bd6] via-[#ee23aa] to-[#f6b73c] px-4 py-2.5 text-sm font-bold text-white shadow"
                      >
                        ✍️ Add their story
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* RIGHT COLUMN — memories, kin actions, meta */}
              <div className="space-y-5">
                {/* Shared Memories */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Heart className="w-4 h-4 text-rose-500" />
                    <h4 className="font-serif font-bold text-slate-900">
                      Shared Memories ({memberMemories.length})
                    </h4>
                  </div>
                  {memberMemories.length === 0 ? (
                    <div className="text-xs text-slate-500 italic mb-2">
                      No memories yet. Be the first to share one.
                    </div>
                  ) : (
                    <div className="space-y-2 mb-2">
                      {memberMemories.slice(0, 5).map((mm) => (
                        <div
                          key={mm.id}
                          className="rounded-lg bg-rose-50 border border-rose-100 px-3 py-2"
                        >
                          <div className="text-sm text-slate-800 whitespace-pre-wrap">{mm.text}</div>
                          <div className="text-[10px] text-rose-600 mt-1">
                            {new Date(mm.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Textarea
                      value={memText}
                      onChange={(e) => setMemText(e.target.value)}
                      placeholder={`Share a memory or story about ${(member?.name || 'Family Member').split(' ')[0]}…`}
                      rows={2}
                      className="text-sm"
                    />
                    <Button
                      type="button"
                      onClick={submitMemory}
                      disabled={!memText.trim() || savingMem}
                      size="sm"
                      className="bg-rose-500 hover:bg-rose-600 text-white self-end"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Quick add relatives */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <UserPlus className="w-4 h-4 text-fuchsia-600" />
                    <h4 className="font-serif font-bold text-slate-900">
                      Add a relative connected to {(member?.name || 'Family Member').split(' ')[0]}
                    </h4>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => {
                        onAddRelative({ relationship: parentRelFor(member), side: sideForKin });
                        onClose();
                      }}
                      className="rounded-lg border border-fuchsia-200 bg-white hover:bg-fuchsia-50 px-3 py-2 text-xs font-semibold text-fuchsia-700 text-left transition"
                    >
                      + Add a parent
                    </button>
                    <button
                      onClick={() => {
                        onAddRelative({ relationship: member.relationship, side: sideForKin });
                        onClose();
                      }}
                      className="rounded-lg border border-fuchsia-200 bg-white hover:bg-fuchsia-50 px-3 py-2 text-xs font-semibold text-fuchsia-700 text-left transition"
                    >
                      + Add a sibling
                    </button>
                    <button
                      onClick={() => {
                        onAddRelative({ relationship: member.relationship, side: sideForKin });
                        onClose();
                      }}
                      className="rounded-lg border border-fuchsia-200 bg-white hover:bg-fuchsia-50 px-3 py-2 text-xs font-semibold text-fuchsia-700 text-left transition"
                    >
                      + Add a spouse
                    </button>
                    <button
                      onClick={() => {
                        onAddRelative({ relationship: childRelFor(member), side: sideForKin });
                        onClose();
                      }}
                      className="rounded-lg border border-fuchsia-200 bg-white hover:bg-fuchsia-50 px-3 py-2 text-xs font-semibold text-fuchsia-700 text-left transition"
                    >
                      + Add a child
                    </button>
                  </div>
                </div>

                <div className="text-[11px] text-slate-500">
                  Added by{' '}
                  <span className="font-semibold">
                    {member.createdBy === member.ownerUserId ? youName : 'a family member'}
                  </span>{' '}
                  on {new Date(member.createdAt).toLocaleDateString()} ·{' '}
                  {member.confirmationsCount} confirmation
                  {member.confirmationsCount === 1 ? '' : 's'}
                </div>
              </div>
            </div>
          )}



          {/* MEDIA TAB */}
          {activeTab === 'media' && (
            <div className="space-y-5">
              {/* Photos */}
              <div>
                <div className="flex items-center gap-2 justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <ImageIcon className="w-4 h-4 text-fuchsia-600" />
                    <h4 className="font-serif font-bold text-slate-900">
                      Photos ({memberPhotos.length})
                    </h4>
                  </div>
                  <button
                    type="button"
                    onClick={openPhotoUpload}
                    className="inline-flex items-center gap-1 rounded-lg bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:opacity-95 text-white text-xs font-bold px-3 py-1.5 shadow"
                  >
                    <ImagePlus className="w-3.5 h-3.5" />
                    Add photos
                  </button>
                </div>
                {memberPhotos.length === 0 ? (
                  <button
                    type="button"
                    onClick={openPhotoUpload}
                    className="w-full rounded-xl border-2 border-dashed border-fuchsia-300 bg-fuchsia-50/40 hover:bg-fuchsia-50 px-4 py-6 flex flex-col items-center justify-center gap-1 text-center text-fuchsia-700"
                  >
                    <Upload className="w-5 h-5" />
                    <div className="text-sm font-bold">Add the first photos</div>
                    <div className="text-[11px] text-slate-500">
                      Pick multiple photos at once · max 15 MB each
                    </div>
                  </button>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-2">
                    {memberPhotos.map((p, idx) => (
                      <div
                        key={p.id}
                        className="relative group rounded-lg overflow-hidden bg-slate-100 aspect-square cursor-pointer"
                        onClick={() => setLightbox({ items: memberPhotos, index: idx })}
                      >
                        <img
                          src={p.url}
                          alt={p.caption || 'Family photo'}
                          className="w-full h-full object-cover transition group-hover:scale-105"
                        />
                        {/* Privacy badge — shown on every photo so the
                            owner can tell at a glance who can see it. RLS
                            still enforces who can actually load the row.
                            "Family" gets a subtle pill; "Only me" gets a
                            stronger one because it's the restrictive case.
                            'shared' is legacy (old rows from before the
                            simplified two-option chooser) and rendered as
                            a generic "Shared" pill. */}
                        <div
                          className={`absolute top-1 left-1 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold shadow ${
                            p.privacy === 'private'
                              ? 'bg-slate-900/85 text-white'
                              : p.privacy === 'shared'
                              ? 'bg-amber-500/95 text-white'
                              : 'bg-white/90 text-slate-700'
                          }`}
                        >
                          {p.privacy === 'private' ? (
                            <>
                              <Lock className="w-2.5 h-2.5" />
                              Only me
                            </>
                          ) : p.privacy === 'shared' ? (
                            <>
                              <Share2 className="w-2.5 h-2.5" />
                              Shared
                            </>
                          ) : (
                            <>
                              <UsersIcon className="w-2.5 h-2.5" />
                              Family
                            </>
                          )}
                        </div>

                        {p.caption && (
                          <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent text-[10px] text-white px-2 py-1 truncate opacity-0 group-hover:opacity-100 transition">
                            {p.caption}
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={async (e) => {
                            e.stopPropagation();
                            await handleDeleteProfileMedia(
                              p.id,
                              'photo',
                            );
                          }}
                          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-rose-500/90 hover:bg-rose-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                          aria-label="Remove photo"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Videos */}
              <div>
                <div className="flex items-center gap-2 justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Film className="w-4 h-4 text-purple-600" />
                    <h4 className="font-serif font-bold text-slate-900">
                      Videos ({memberVideos.length})
                    </h4>
                  </div>
                  <button
                    type="button"
                    onClick={openVideoUpload}
                    className="inline-flex items-center gap-1 rounded-lg bg-gradient-to-r from-purple-600 to-fuchsia-500 hover:opacity-95 text-white text-xs font-bold px-3 py-1.5 shadow"
                  >
                    <VideoIcon className="w-3.5 h-3.5" />
                    Add videos
                  </button>
                </div>
                {memberVideos.length === 0 ? (
                  <button
                    type="button"
                    onClick={openVideoUpload}
                    className="w-full rounded-xl border-2 border-dashed border-purple-300 bg-purple-50/40 hover:bg-purple-50 px-4 py-6 flex flex-col items-center justify-center gap-1 text-center text-purple-700"
                  >
                    <Upload className="w-5 h-5" />
                    <div className="text-sm font-bold">Add the first videos</div>
                    <div className="text-[11px] text-slate-500">
                      Pick multiple videos at once · max 200 MB each
                    </div>
                  </button>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-2">
                    {memberVideos.map((v, idx) => (
                      <div
                        key={v.id}
                        className="relative group rounded-lg overflow-hidden bg-black aspect-video cursor-pointer"
                        onClick={() => setLightbox({ items: memberVideos, index: idx })}
                      >
                        <video
                          src={v.url}
                          playsInline
                          muted
                          preload="metadata"
                          className="w-full h-full object-cover pointer-events-none"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition">
                          <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center shadow">
                            <Film className="w-5 h-5 text-purple-700" />
                          </div>
                        </div>
                        {/* Privacy badge — same logic as photo tiles. RLS
                            on family_graph_photos still gates row access. */}
                        <div
                          className={`absolute top-1 left-1 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-bold shadow ${
                            v.privacy === 'private'
                              ? 'bg-slate-900/85 text-white'
                              : v.privacy === 'shared'
                              ? 'bg-amber-500/95 text-white'
                              : 'bg-white/90 text-slate-700'
                          }`}
                        >
                          {v.privacy === 'private' ? (
                            <>
                              <Lock className="w-2.5 h-2.5" />
                              Only me
                            </>
                          ) : v.privacy === 'shared' ? (
                            <>
                              <Share2 className="w-2.5 h-2.5" />
                              Shared
                            </>
                          ) : (
                            <>
                              <UsersIcon className="w-2.5 h-2.5" />
                              Family
                            </>
                          )}
                        </div>

                        <button
                          type="button"
                          onClick={async (e) => {
                            e.stopPropagation();
                            await handleDeleteProfileMedia(
                              v.id,
                              'video',
                            );
                          }}
                          className="absolute top-1 right-1 w-6 h-6 rounded-full bg-rose-500/90 hover:bg-rose-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                          aria-label="Remove video"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>

                    ))}
                  </div>
                )}
              </div>
            </div>
          )}


          {/* JOURNEY TAB — chronological timeline */}
          {activeTab === 'journey' && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <ClockIcon className="w-4 h-4 text-fuchsia-600" />
                <h4 className="font-serif font-bold text-slate-900">
                  Journey through {(member?.name || 'Family Member').split(' ')[0]}'s life
                </h4>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Photos, videos, memories, birthdays, and stories — sorted by date.
                Add a memory to mark a moment in their life.
              </p>

              {journey.length === 0 ? (
                <div className="text-sm text-slate-500 italic text-center py-8 border border-dashed border-slate-200 rounded-xl">
                  Their journey hasn't been written yet. Share the first memory below.
                </div>
              ) : (
                <div className="space-y-4">
                  {LIFE_STAGES.map((stage) => {
                    const items = journey.filter((j) => j.stage === stage.id);
                    if (items.length === 0) return null;
                    return (
                      <div key={stage.id}>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-fuchsia-700 mb-2 flex items-center gap-2">
                          <span className="h-px flex-1 bg-fuchsia-200" />
                          {stage.label}
                          <span className="text-slate-400 font-normal">({items.length})</span>
                          <span className="h-px flex-1 bg-fuchsia-200" />
                        </div>
                        <ol className="relative border-l-2 border-fuchsia-200 ml-3 space-y-3">
                          {items.map((j) => (
                            <li key={j.id} className="ml-4 relative">
                              <span className={`absolute -left-[22px] top-1 w-4 h-4 rounded-full ring-2 ring-white ${
                                j.kind === 'birthday' ? 'bg-amber-400' :
                                j.kind === 'memory' ? 'bg-rose-500' :
                                j.kind === 'story' ? 'bg-amber-500' :
                                j.kind === 'life_event' ? 'bg-emerald-500' :
                                j.kind === 'photo' ? 'bg-fuchsia-500' :
                                j.kind === 'video' ? 'bg-purple-500' :
                                j.kind === 'biography' ? 'bg-slate-500' : 'bg-emerald-500'
                              }`} />
                              <div className="rounded-lg bg-white border border-slate-200 px-3 py-2">
                                <div className="flex items-center justify-between gap-2">
                                  <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{j.title}</div>
                                  <div className="text-[10px] text-slate-400">
                                    {j.hasDate && j.date ? j.date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : 'Undated'}
                                  </div>
                                </div>
                                {j.location && (
                                  <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1">
                                    <MapPin className="w-3 h-3" /> {j.location}
                                  </div>
                                )}
                                {j.body && <div className="text-sm text-slate-800 mt-1 whitespace-pre-wrap">{j.body}</div>}
                                {j.url && j.kind === 'photo' && (
                                  isSafeMediaUrl(j.url) ? (
                                    <img
                                      src={j.url}
                                      alt=""
                                      className="mt-2 rounded-md w-32 h-32 object-cover bg-slate-100"
                                      onError={(e) => {
                                        // Swap the broken <img> for a polite
                                        // text fallback so a stale storage
                                        // path can't blank the timeline.
                                        const el = e.currentTarget;
                                        const parent = el.parentElement;
                                        if (!parent) return;
                                        el.style.display = 'none';
                                        const note = document.createElement('div');
                                        note.className =
                                          'mt-2 rounded-md w-32 h-32 flex items-center justify-center text-[10px] text-slate-500 bg-slate-50 border border-dashed border-slate-300 px-2 text-center';
                                        note.textContent = 'This memory could not be loaded.';
                                        parent.appendChild(note);
                                      }}
                                    />
                                  ) : (
                                    <div className="mt-2 rounded-md w-32 h-32 flex items-center justify-center text-[10px] text-slate-500 bg-slate-50 border border-dashed border-slate-300 px-2 text-center">
                                      This memory could not be loaded.
                                    </div>
                                  )
                                )}
                                {j.url && j.kind === 'video' && (
                                  isSafeMediaUrl(j.url) ? (
                                    <video
                                      src={j.url}
                                      controls
                                      playsInline
                                      preload="metadata"
                                      className="mt-2 rounded-md w-full max-w-xs aspect-video bg-black"
                                      onError={(e) => {
                                        const el = e.currentTarget;
                                        const parent = el.parentElement;
                                        if (!parent) return;
                                        el.style.display = 'none';
                                        const note = document.createElement('div');
                                        note.className =
                                          'mt-2 rounded-md w-full max-w-xs aspect-video flex items-center justify-center text-xs text-slate-500 bg-slate-50 border border-dashed border-slate-300 px-3 text-center';
                                        note.textContent = 'This memory could not be loaded.';
                                        parent.appendChild(note);
                                      }}
                                    />
                                  ) : (
                                    <div className="mt-2 rounded-md w-full max-w-xs aspect-video flex items-center justify-center text-xs text-slate-500 bg-slate-50 border border-dashed border-slate-300 px-3 text-center">
                                      This memory could not be loaded.
                                    </div>
                                  )
                                )}

                                {j.entryType && j.entryId && (
                                  <CommentsThread memberId={member.id} entryType={j.entryType} entryId={j.entryId} compact />
                                )}
                              </div>
                            </li>
                          ))}
                        </ol>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="pt-3 border-t border-slate-200">
                <div className="text-xs font-bold text-slate-700 mb-2">Add a moment to their journey</div>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={openPhotoUpload} className="rounded-lg border border-fuchsia-200 bg-fuchsia-50 hover:bg-fuchsia-100 text-fuchsia-700 px-3 py-2 text-xs font-bold">Upload Photo</button>
                  <button onClick={openVideoUpload} className="rounded-lg border border-purple-200 bg-purple-50 hover:bg-purple-100 text-purple-700 px-3 py-2 text-xs font-bold">Upload Video</button>
                  <button onClick={() => openComposer('story')} className="rounded-lg border border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-700 px-3 py-2 text-xs font-bold">Add Story</button>
                  <button onClick={() => openComposer('life_event')} className="rounded-lg border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 px-3 py-2 text-xs font-bold">Add Life Event</button>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Sticky footer actions */}
        <div className="border-t border-slate-200 bg-white p-3 flex flex-wrap gap-2">
          {!verified && (
            <Button size="sm" onClick={handleConfirm} disabled={confirming} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <CheckCheck className="w-4 h-4 mr-1" /> Confirm relationship
            </Button>
          )}
          {isStaff && pending && (
            <>
              <Button size="sm" onClick={handleApprove} className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white">Approve</Button>
              <Button size="sm" variant="outline" onClick={handleReject} className="border-rose-300 text-rose-700 hover:bg-rose-50">Reject</Button>
            </>
          )}
          {canShowRemoveAction && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setDeletionErrorClass('');
                setDeletionExplanation('');
                setRemoveErroneousRecordOpen(true);
              }}
              className="border-rose-300 text-rose-700 hover:bg-rose-50 ml-auto"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Remove erroneous record
            </Button>
          )}
        </div>
      </div>

      {removeErroneousRecordOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4"
          onClick={() => {
            if (!removingErroneousRecord) {
              setRemoveErroneousRecordOpen(false);
            }
          }}
        >
          <div
            className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl"
            onClick={(event) =>
              event.stopPropagation()
            }
          >
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-rose-100 p-2 text-rose-700">
                <Trash2 className="h-5 w-5" />
              </div>

              <div>
                <h3 className="text-lg font-bold text-slate-900">
                  Remove erroneous record
                </h3>

                <p className="mt-1 text-sm text-slate-600">
                  Legitimate family members and family history cannot be deleted.
                  Use this only when the record never represented a legitimate
                  canonical family member.
                </p>
              </div>
            </div>

            <div className="mt-5 space-y-4">
              <label className="block">
                <span className="text-sm font-semibold text-slate-800">
                  Error classification
                </span>

                <select
                  value={deletionErrorClass}
                  onChange={(event) =>
                    setDeletionErrorClass(
                      event.target.value as
                        | Exclude<
                            CanonicalTreeDeletionErrorClass,
                            'FAILED_CREATION'
                          >
                        | '',
                    )
                  }
                  disabled={removingErroneousRecord}
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
                >
                  <option value="">
                    Select a classification
                  </option>
                  <option value="ACCIDENTAL_DUPLICATE">
                    Accidental duplicate
                  </option>
                  <option value="WRONG_PERSON">
                    Wrong person
                  </option>
                  <option value="EMPTY_PLACEHOLDER">
                    Empty placeholder
                  </option>
                  <option value="TEST_RECORD">
                    Test record
                  </option>
                </select>
              </label>

              <label className="block">
                <span className="text-sm font-semibold text-slate-800">
                  Explanation
                </span>

                <Textarea
                  value={deletionExplanation}
                  onChange={(event) =>
                    setDeletionExplanation(
                      event.target.value,
                    )
                  }
                  disabled={removingErroneousRecord}
                  className="mt-1 min-h-28"
                  placeholder="Explain how this erroneous record was created and why it never represented a legitimate family member."
                />
              </label>

              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                <p className="text-xs font-semibold text-amber-900">
                  Is this a legitimate person whose profile or relationship is wrong?
                </p>

                <div className="mt-2 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setRemoveErroneousRecordOpen(false);
                      setEditProfileOpen(true);
                    }}
                  >
                    Edit Profile
                  </Button>

                  {onEditRelationship && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setRemoveErroneousRecordOpen(false);
                        onEditRelationship(member);
                      }}
                    >
                      Correct Relationship
                    </Button>
                  )}

                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setRemoveErroneousRecordOpen(false);
                      toast.info(
                        'Use the governed duplicate merge workflow when both records represent the same legitimate person.',
                      );
                    }}
                  >
                    Merge Duplicate
                  </Button>
                </div>
              </div>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={removingErroneousRecord}
                onClick={() =>
                  setRemoveErroneousRecordOpen(false)
                }
              >
                Cancel
              </Button>

              <Button
                type="button"
                onClick={handleDelete}
                disabled={
                  removingErroneousRecord ||
                  !deletionErrorClass ||
                  !deletionExplanation.trim()
                }
                className="bg-rose-600 text-white hover:bg-rose-700"
              >
                {removingErroneousRecord ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Removing…
                  </>
                ) : (
                  'Remove erroneous record'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Rich memory / story / life-event composer modal */}
      {composerOpen && (
        <MemoryComposerModal
          open={composerOpen}
          member={member}
          initialKind={composerKind}
          onClose={() => setComposerOpen(false)}
        />
      )}

      {/* Multi-file photo / video upload modal — opened from the action bar
          and the Media tab + Journey tab "Upload" buttons. */}
      {mediaModal && (
        <MediaUploadModal
          open={!!mediaModal}
          kind={mediaModal}
          memberId={member.id}
          memberName={displayName}
          ownerUserId={user?.id ?? null}
          setAvatarIfMissing
          hasAvatar={!!member.photoUrl}
          maxUploadItems={
            mediaModal === 'image'
              ? profileAuthority.remainingPhotoCount
              : null
          }
          authorityGranted={
            mediaModal === 'image'
              ? profileAuthority.canUploadPhotos
              : profileAuthority.canUploadVideos
          }
          onClose={() => setMediaModal(null)}
        />
      )}


      {/* Full-screen lightbox for tapping a photo or video tile. */}
      {lightbox && (
        <MediaLightbox
          items={lightbox.items}
          startIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}

      {/* Governed personal-profile editor. */}
      {editProfileOpen && canEditProfile && (
        <EditProfileModal
          open={editProfileOpen}
          member={member}
          ownerUserId={
            member.ownerUserId ??
            user?.id ??
            null
          }
          fallbackName={
            isMyProfile
              ? youName
              : member.name || 'Family Member'
          }
          canUploadProfilePhoto={
            profileAuthority.canUploadPhotos ||
            Boolean(member.photoUrl)
          }
          remainingPhotoCount={
            profileAuthority.remainingPhotoCount
          }
          onClose={() => setEditProfileOpen(false)}
        />
      )}

      {/* Owner / admin "Connect Existing User to This Tree" repair modal —
          shown only when the staff button above was tapped. The modal calls
          the connect-user-to-profile edge function which writes ONLY the
          claim metadata (claimed_by_user_id / profile_state / claimed_at /
          updated_at). It NEVER touches parent_ids, relationship, side,
          photos, memories, biography, or anything else on the row. */}
      {connectUserOpen && (
        <ConnectUserModal
          open={connectUserOpen}
          member={member}
          onClose={() => setConnectUserOpen(false)}
        />
      )}
    </div>



  );
};

export default ProfileDetailDrawer;
